import type {} from "react";

export type PriceMode = "random" | "real" | "pvp";

export interface RoundFeedInfo {
  readonly mode: PriceMode;
  readonly coinId?: string;
  readonly coinLabel?: string;
  readonly startIso?: string;
  readonly endIso?: string;
}

export interface PriceSource {
  readonly mode: PriceMode;
  next(previousPrice: number, currentTimeMs: number): number;
  peekCurrent(previousPrice: number): number;
  // Peek next N prices without advancing the cursor. For random mode may return empty.
  peekFuturePrices(count: number): ReadonlyArray<number>;
  getInfo(): Readonly<RoundFeedInfo>;
}

interface ChartConfigLike {
  readonly startPrice: number;
  readonly volatility: number;
  readonly trendStrength: number;
}

interface CreateOptions {
  readonly chartConfig: ChartConfigLike;
  readonly approximateNeededPoints?: number;
}

// Utility: capitalize coin label nicely from id
function formatCoinLabel(coinId: string): string {
  if (!coinId) return "";
  return coinId
    .split(/[ _-]+/g)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
}

// Random walk implementation (previous default behaviour)
class RandomPriceSource implements PriceSource {
  public readonly mode: PriceMode = "random";
  private readonly cfg: ChartConfigLike;

  constructor(cfg: ChartConfigLike) {
    this.cfg = cfg;
  }

  public next(previousPrice: number): number {
    const randomChange = (Math.random() - 0.5) * 2 * this.cfg.volatility;
    const trendChange = this.cfg.trendStrength;
    const eventChance = 0.003;
    let eventMultiplier = 1;
    if (Math.random() < eventChance) {
      eventMultiplier = Math.random() > 0.5 ? 1.15 : 0.85;
    }
    const newPrice =
      previousPrice * (1 + randomChange + trendChange) * eventMultiplier;
    return Math.max(10, newPrice);
  }

  public peekCurrent(previousPrice: number): number {
    return previousPrice || this.cfg.startPrice;
  }

  public peekFuturePrices(count: number): ReadonlyArray<number> {
    // Random source has no deterministic future; return empty
    return [];
  }

  public getInfo(): Readonly<RoundFeedInfo> {
    return { mode: this.mode };
  }
}

// Real coin CSV-backed implementation
interface CsvRow {
  coinId: string;
  tsMs: number;
  tsIso: string;
  price: number;
}

function parseCsv(raw: string): ReadonlyArray<CsvRow> {
  const lines = raw.trim().split(/\r?\n/);
  const out: CsvRow[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    if (i === 0 && line.startsWith("coin_id")) continue;
    const parts = line.split(",");
    if (parts.length < 4) continue;
    const coinId = parts[0];
    const tsMs = Number(parts[1]);
    const tsIso = parts[2];
    const price = Number(parts[3]);
    if (!isFinite(tsMs) || !isFinite(price)) continue;
    out.push({ coinId, tsMs, tsIso, price });
  }
  return out;
}

// Load all CSVs from data folder at build time
const csvModules: Record<string, string> = import.meta.glob(
  "../../../data/*.csv",
  { query: "?raw", import: "default", eager: true }
) as unknown as Record<string, string>;

interface Dataset {
  coinId: string;
  coinLabel: string;
  rows: ReadonlyArray<CsvRow>;
}

const DATASETS: ReadonlyArray<Dataset> = Object.entries(csvModules)
  .map(([path, raw]) => {
    const rows = parseCsv(raw);
    const coinId =
      rows[0]?.coinId || path.split("/").pop()?.split("_")?.[0] || "";
    return {
      coinId,
      coinLabel: formatCoinLabel(coinId),
      rows,
    } as Dataset;
  })
  .filter((d) => d.rows.length > 0);

export const REAL_COIN_CONFIG = DATASETS.map((d) => ({
  coinId: d.coinId,
  coinLabel: d.coinLabel,
})) as ReadonlyArray<{ coinId: string; coinLabel: string }>;

class RealCoinPriceSource implements PriceSource {
  public readonly mode: PriceMode;
  private readonly dataset: Dataset;
  private readonly startIndex: number;
  private index: number;
  private info: RoundFeedInfo;

  constructor(dataset: Dataset, startIndex: number, mode: PriceMode = "real") {
    this.dataset = dataset;
    this.startIndex = Math.max(
      0,
      Math.min(startIndex, dataset.rows.length - 1)
    );
    this.index = this.startIndex;
    this.mode = mode;
    const startRow = dataset.rows[this.startIndex];
    this.info = {
      mode,
      coinId: dataset.coinId,
      coinLabel: dataset.coinLabel,
      startIso: startRow?.tsIso,
      endIso: startRow?.tsIso,
    };
  }

  public next(): number {
    const row = this.dataset.rows[this.index];
    const price =
      row?.price ?? this.dataset.rows[this.dataset.rows.length - 1].price;
    this.info = { ...this.info, endIso: row?.tsIso ?? this.info.endIso };
    this.index = Math.min(this.index + 1, this.dataset.rows.length - 1);
    return price;
  }

  public peekCurrent(previousPrice: number): number {
    if (this.index >= 0 && this.index < this.dataset.rows.length) {
      return this.dataset.rows[this.index].price;
    }
    return previousPrice;
  }

  public peekFuturePrices(count: number): ReadonlyArray<number> {
    if (count <= 0) return [];
    const start = Math.max(0, this.index);
    const end = Math.min(this.dataset.rows.length, start + count);
    const slice = this.dataset.rows.slice(start, end);
    return slice.map((r) => r.price);
  }

  public getInfo(): Readonly<RoundFeedInfo> {
    return this.info;
  }
}

function pickRandomDataset(): Dataset | null {
  if (DATASETS.length === 0) return null;
  const idx = Math.floor(Math.random() * DATASETS.length);
  return DATASETS[idx];
}

export function createPriceSource(
  mode: PriceMode,
  options: CreateOptions
): PriceSource {
  if (mode === "real" || mode === "pvp") {
    const ds = pickRandomDataset();
    if (!ds) {
      // Fallback to random if no datasets
      return new RandomPriceSource(options.chartConfig);
    }
    const need = Math.max(1, options.approximateNeededPoints ?? 300);
    const maxStart = Math.max(0, ds.rows.length - need - 1);
    const startIndex = Math.floor(Math.random() * (maxStart + 1));
    return new RealCoinPriceSource(ds, startIndex, mode);
  }
  return new RandomPriceSource(options.chartConfig);
}
